import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Collapse,
  IconButton,
  TablePagination,
} from "@mui/material";
import { TimelineOutlined } from "@mui/icons-material";
import styled from "styled-components";
import {
  KeyboardArrowRight,
  KeyboardArrowDown,
  FormatListBulleted,
} from "@mui/icons-material";
import { GetPatientVitalSigns } from "../../api/Patient";
import api from "../../api/config";
import Timeline from "./Timeline";
const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  padding: 0 16px;
`;

const StyledButton = styled(IconButton)`
  background-color: ${(props) => (props.active ? "#7efbfd" : "#3b4252")};
  color: ${(props) => (props.active ? "#3b4252" : "white")};
  font-size: 14px;
  font-weight: bold;
  padding: 8px;
  border: 1px solid #7efbfd;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 150px;
  text-align: center;

  border-top-left-radius: ${(props) => (props.text === "left" ? "50px" : "0")};
  border-bottom-left-radius: ${(props) =>
    props.text === "left" ? "50px" : "0"};
  border-top-right-radius: ${(props) =>
    props.text === "right" ? "50px" : "0"};
  border-bottom-right-radius: ${(props) =>
    props.text === "right" ? "50px" : "0"};

  &:hover {
    background-color: ${(props) => (props.active ? "#66d9c9" : "#444e6b")};
  }
`;

const InfoTable = styled.div`
  background-color: #3b4252;
  padding: 16px;
  border-radius: 8px;
  height: 70vh;
  overflow-y: auto;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background: #1f2a36;
    border-radius: 100px;
  }
  &::-webkit-scrollbar-thumb {
    background: #A1A5AC;
    border: 1px solid #A1A5AC;
    border-radius: 100px;
  }
`;

const PatientEHR = ({ patient }) => {
  const [vitalSigns, setVitalSigns] = useState([]);
  const [vitalSignsLoading, setVitalSignsLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [selectedButton, setSelectedButton] = useState("Table");

  const handleButtonClick = (button) => {
    setSelectedButton(button);
  };

  const toggleRowExpansion = (studyId) => {
    setExpandedRows((prevExpandedRows) =>
      prevExpandedRows.includes(studyId)
        ? prevExpandedRows.filter((id) => id !== studyId)
        : [...prevExpandedRows, studyId]
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const fetchPatientVitalSigns = async () => {
      try {
        console.log("Fetching patient vital signs", patient.uuid);
        const response = await api.get(
          `${GetPatientVitalSigns}/${patient.uuid}?simplify=true`
        );
        setVitalSigns(response.data);
      } catch (error) {
        console.error("Failed to fetch patient vital signs", error);
      } finally {
        setVitalSignsLoading(false);
      }
    };
    fetchPatientVitalSigns();
  }, [patient.uuid]);

  const sortedVitalSigns = [...vitalSigns].sort((a, b) => {
    const dateA = new Date(a.effectiveDateTime);
    const dateB = new Date(b.effectiveDateTime);
    return dateB - dateA;
  });

  const paginatedVitalSigns = sortedVitalSigns.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div style={{ marginTop: "16px" }}>
      <ButtonContainer>
        <IconButton
          sx={{
            backgroundColor:
              selectedButton === "Timeline" ? "#7efbfd" : "#3b4252",
            color: selectedButton === "Timeline" ? "#3b4252" : "white",
            fontSize: "14px",
            fontWeight: "bold",
            padding: "8px",
            border: "1px solid #7efbfd",
            cursor: "pointer",
            transition: "all 0.3s ease",
            width: "150px",
            textAlign: "center",
            borderTopLeftRadius: "50px",
            borderBottomLeftRadius: "50px",
            borderTopRightRadius: "0px",
            borderBottomRightRadius: "0px",
            "&:hover": {
              backgroundColor:
                selectedButton === "Timeline" ? "#66d9c9" : "#444e6b",
            },
          }}
          onClick={() => handleButtonClick("Timeline")}
        >
          <TimelineOutlined sx={{ marginRight: "8px" }} />
          Timeline
        </IconButton>

        <IconButton
          sx={{
            backgroundColor: selectedButton === "Table" ? "#7efbfd" : "#3b4252",
            color: selectedButton === "Table" ? "#3b4252" : "white",
            fontSize: "14px",
            fontWeight: "bold",
            padding: "8px",
            border: "1px solid #7efbfd",
            cursor: "pointer",
            transition: "all 0.3s ease",
            width: "150px",
            textAlign: "center",
            borderTopRightRadius: "50px",
            borderBottomRightRadius: "50px",
            borderTopLeftRadius: "0px",
            borderBottomLeftRadius: "0px",
            "&:hover": {
              backgroundColor:
                selectedButton === "Table" ? "#66d9c9" : "#444e6b",
            },
          }}
          onClick={() => handleButtonClick("Table")}
        >
          <FormatListBulleted sx={{ marginRight: "8px" }} />
          Table
        </IconButton>
      </ButtonContainer>

      <InfoTable>
        {selectedButton === "Timeline" ? (
          <Timeline selectedPatient={patient} vitalSigns={sortedVitalSigns} />
        ) : (
          <div>
            <Table stickyHeader>
              {vitalSignsLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={3} style={{ textAlign: "center" }}>
                      <CircularProgress style={{ color: "#FBFEFF" }} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {paginatedVitalSigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} style={{ textAlign: "center" }}>
                        No EHR data for the patient
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedVitalSigns.map((vital, index) => (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell style={{ padding: "2px" }}>
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpansion(index)}
                            >
                              {expandedRows.includes(index) ? (
                                <KeyboardArrowDown
                                  style={{ filter: "invert(1)" }}
                                />
                              ) : (
                                <KeyboardArrowRight
                                  style={{ filter: "invert(1)" }}
                                />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            {new Date(vital.effectiveDateTime).toLocaleString()}
                          </TableCell>
                          <TableCell>{vital.status}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                          >
                            <Collapse
                              in={expandedRows.includes(index)}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Table size="small">
                                <TableBody>
                                  {vital.components.map(
                                    (component, compIndex) => (
                                      <TableRow key={compIndex}>
                                        <TableCell>{component.code}</TableCell>
                                        <TableCell>{`${component.value} ${component.unit}`}</TableCell>
                                      </TableRow>
                                    )
                                  )}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              )}
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={sortedVitalSigns.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        )}
      </InfoTable>
    </div>
  );
};

export default PatientEHR;
